import React from 'react';
import { Card, CardContent } from '@/admin/components/ui/card';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconEye,
  IconFilter,
  IconX,
} from '@tabler/icons-react';
import { fetchAuditLogs } from '@/admin/data/mock';
import type { AuditLog } from '@/admin/data/mock';

const getActionBadgeVariant = (action: string) => {
  if (action.includes('created') || action.includes('issued'))
    return 'default';
  if (action.includes('deleted') || action.includes('revoked'))
    return 'destructive';
  if (action.includes('updated') || action.includes('renewed'))
    return 'secondary';
  return 'outline';
};

export const AuditPage: React.FC = () => {
  const [logs, setLogs] = React.useState<AuditLog[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [selectedLog, setSelectedLog] = React.useState<AuditLog | null>(null);
  
  // Filter states
  const [actionFilter, setActionFilter] = React.useState<string>('all');
  const [subjectTypeFilter, setSubjectTypeFilter] = React.useState<string>('all');
  const [timeFilter, setTimeFilter] = React.useState<string>('all');

  React.useEffect(() => {
    fetchAuditLogs()
      .then(data => setLogs(data))
      .finally(() => setLoading(false));
  }, []);

  // Extract unique action types and subject types from logs
  const actionTypes = React.useMemo(() => {
    const types = new Set(logs.map(log => log.action));
    return Array.from(types).sort();
  }, [logs]);

  const subjectTypes = React.useMemo(() => {
    const types = new Set(
      logs.map(log => log.subject.split(':')[0])
    );
    return Array.from(types).sort();
  }, [logs]);

  const filteredLogs = React.useMemo(() => {
    return logs.filter(log => {
      // Action filter
      const matchesAction =
        actionFilter === 'all' || log.action === actionFilter;

      // Subject type filter
      const matchesSubjectType =
        subjectTypeFilter === 'all' ||
        log.subject.startsWith(subjectTypeFilter + ':');

      // Time filter
      const matchesTime = (() => {
        if (timeFilter === 'all') return true;
        
        const now = new Date();
        const logDate = new Date(log.createdAt);
        const diffInHours = (now.getTime() - logDate.getTime()) / (1000 * 60 * 60);

        switch (timeFilter) {
          case 'last1h':
            return diffInHours <= 1;
          case 'last6h':
            return diffInHours <= 6;
          case 'last24h':
            return diffInHours <= 24;
          case 'last7d':
            return diffInHours <= 24 * 7;
          case 'last30d':
            return diffInHours <= 24 * 30;
          default:
            return true;
        }
      })();

      return matchesAction && matchesSubjectType && matchesTime;
    });
  }, [logs, actionFilter, subjectTypeFilter, timeFilter]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredLogs.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [actionFilter, subjectTypeFilter, timeFilter]);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const clearFilters = () => {
    setActionFilter('all');
    setSubjectTypeFilter('all');
    setTimeFilter('all');
  };

  const hasActiveFilters =
    actionFilter !== 'all' || subjectTypeFilter !== 'all' || timeFilter !== 'all';

  return (
    <div className="flex h-full flex-col">
      {/* Header Section - Fixed Height */}
      <div className="flex-shrink-0 bg-background px-4 py-4 lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <IconFilter className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">
                Filters:
              </span>
            </div>

            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionTypes.map(action => (
                  <SelectItem key={action} value={action}>
                    {action}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={subjectTypeFilter}
              onValueChange={setSubjectTypeFilter}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Subject Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {subjectTypes.map(type => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={timeFilter} onValueChange={setTimeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="last1h">Last 1 Hour</SelectItem>
                <SelectItem value="last6h">Last 6 Hours</SelectItem>
                <SelectItem value="last24h">Last 24 Hours</SelectItem>
                <SelectItem value="last7d">Last 7 Days</SelectItem>
                <SelectItem value="last30d">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="gap-1"
              >
                <IconX className="size-4" />
                Clear Filters
              </Button>
            )}
          </div>

          {/* Pagination Controls */}
          {!loading && filteredLogs.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {startIndex + 1}-{Math.min(endIndex, filteredLogs.length)} of{' '}
                {filteredLogs.length}
              </span>
              <Select
                value={pageSize.toString()}
                onValueChange={value => {
                  setPageSize(Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  <IconChevronsLeft className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <IconChevronLeft className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <IconChevronRight className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <IconChevronsRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table Section - Scrollable */}
      <div className="flex-1 overflow-auto px-4 lg:px-6">
        <Card className="border-0 shadow-none">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">ID</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead className="w-[80px] text-right">
                      Details
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="h-32 text-center text-muted-foreground"
                      >
                        Loading audit records...
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && paginatedLogs.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="h-32 text-center text-muted-foreground"
                      >
                        {hasActiveFilters
                          ? 'No matching audit entries'
                          : 'No audit entries yet'}
                      </TableCell>
                    </TableRow>
                  )}
                  {paginatedLogs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-xs">
                        #{log.id}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getActionBadgeVariant(log.action)}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {log.subject}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm">
                            {formatRelativeTime(log.createdAt)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(log.createdAt)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedLog(log)}
                          disabled={
                            !log.meta || Object.keys(log.meta).length === 0
                          }
                        >
                          <IconEye className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details Modal */}
      {selectedLog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="mx-4 max-h-[80vh] w-full max-w-2xl overflow-auto rounded-lg bg-card p-6 shadow-lg"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold">Log Details</h2>
                <p className="text-sm text-muted-foreground">
                  Event ID: #{selectedLog.id}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedLog(null)}
              >
                <IconX className="size-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium text-muted-foreground">
                  Action
                </div>
                <div className="mt-1">
                  <Badge variant={getActionBadgeVariant(selectedLog.action)}>
                    {selectedLog.action}
                  </Badge>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">
                  Subject
                </div>
                <div className="mt-1">{selectedLog.subject}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">
                  Timestamp
                </div>
                <div className="mt-1">
                  {formatDateTime(selectedLog.createdAt)}
                </div>
              </div>
              {selectedLog.meta && Object.keys(selectedLog.meta).length > 0 && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground">
                    Metadata
                  </div>
                  <div className="mt-1 rounded-md bg-muted p-3">
                    <pre className="overflow-auto text-xs">
                      {JSON.stringify(selectedLog.meta, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
